import { Cyl, Lathe, Ring } from '../../render/parts'
import { DESK_HEIGHT } from '../parts/worktop'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * Round pedestal meeting table. Single column on a flared disc foot, with a
 * small power module set into the centre of the top.
 *
 * The footprint is declared at the larger Ø1.1; the Ø0.8 top is centred inside
 * it and gets a proportionally smaller foot.
 */

const HEIGHT = DESK_HEIGHT
const TOP = 0.026

interface Size {
  /** Top radius. */
  r: number
  /** Disc foot radius. */
  foot: number
}

const SIZE_D110: Size = { r: 0.55, foot: 0.3 }
const SIZE_D80: Size = { r: 0.4, foot: 0.24 }

const SIZES: Record<string, Size> = {
  d110: SIZE_D110,
  d80: SIZE_D80,
}

function TableRound({ accent, variant = 'd110' }: AssetContext) {
  const { r, foot } = SIZES[variant] ?? SIZE_D110
  const columnBase = 0.042
  const columnTop = 0.7

  return (
    <group>
      {/* Two stacked discs give the edge a stepped profile instead of a slab. */}
      <Cyl tone="surface" r={r} h={TOP} segments={48} position={[0, HEIGHT - TOP / 2, 0]} />
      <Cyl
        tone="shell"
        r={r - 0.01}
        h={0.012}
        segments={48}
        position={[0, HEIGHT - TOP - 0.004, 0]}
      />

      {/* Centre power module: dark cup, accent lid. */}
      <Cyl tone="darkSoft" r={0.055} h={0.014} segments={24} position={[0, HEIGHT - 0.005, 0]} />
      <Cyl
        tone="accent"
        color={accent}
        r={0.028}
        h={0.005}
        segments={20}
        position={[0, HEIGHT + 0.0025, 0]}
      />

      {/* Structure. */}
      <Cyl tone="metal" r={0.11} h={0.028} segments={28} position={[0, 0.696, 0]} />
      <Cyl
        tone="shell"
        r={0.07}
        h={columnTop - columnBase}
        segments={28}
        position={[0, (columnTop + columnBase) / 2, 0]}
      />
      <Lathe
        tone="shell"
        segments={40}
        profileKey={`table-round-foot-${foot}`}
        profile={[
          [0, 0],
          [foot, 0],
          [foot, 0.012],
          [foot - 0.03, 0.03],
          [0.085, 0.05],
          [0, 0.05],
        ]}
      />
      <Ring
        tone="metalDark"
        r={foot - 0.008}
        tube={0.009}
        rotation={[Math.PI / 2, 0, 0]}
        position={[0, 0.009, 0]}
      />
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'table-round',
  name: 'Mesa redonda',
  category: 'tables',
  footprint: [SIZE_D110.r * 2, SIZE_D110.r * 2],
  height: 0.75,
  surfaceHeight: HEIGHT,
  seats: 4,
  occupantSide: 'around',
  accentable: true,
  variants: [
    { id: 'd110', name: 'Ø1.1', footprint: [SIZE_D110.r * 2, SIZE_D110.r * 2] },
    { id: 'd80', name: 'Ø0.8', footprint: [SIZE_D80.r * 2, SIZE_D80.r * 2] },
  ],
  keywords: [
    'mesa',
    'redonda',
    'circular',
    'reunión',
    'pedestal',
    'round table',
    'meeting',
  ],
  Component: TableRound,
}
